import { startTransition, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Router, UNSAFE_createBrowserHistory as createBrowserHistory } from 'react-router-dom';
import i18n, { toSupportedLanguage } from '@/i18n';
import { isCrawler, LANG_META, splitLangPath } from '@/seo/site';
import { SITE_BASE, addressFor, addressLanguage, keepsAddress, withoutSiteBase } from './languageAddress';

const currentLanguage = () => toSupportedLanguage(i18n.resolvedLanguage || i18n.language);
const crawler = typeof navigator !== 'undefined' && isCrawler(navigator.userAgent);

/** A visitor whose language is not Portuguese reads the site at that language's address from the first paint. */
function moveToLanguageAddress() {
  if (crawler) return;
  const target = addressFor(window.location, currentLanguage());
  if (target) window.history.replaceState(window.history.state, '', target);
}

/**
 * BrowserRouter with the language in the address: /es/escunas renders the /escunas page in Spanish, links stay
 * inside the language (the prefix is the router's basename) and the language switcher moves the address with it.
 * Search engines always read Portuguese at the plain addresses (i18n detects them as crawlers).
 */
export default function LangRouter({ children }: { children: ReactNode }) {
  const historyRef = useRef<ReturnType<typeof createBrowserHistory> | null>(null);
  if (!historyRef.current) {
    moveToLanguageAddress();
    historyRef.current = createBrowserHistory({ window, v5Compat: true });
  }
  const history = historyRef.current;
  const [state, setState] = useState({ action: history.action, location: history.location });
  // Set while the language switcher moves the address: that move renders together with the new language.
  const withLanguage = useRef(false);

  // Like BrowserRouter: navigations render in a transition so a lazy page keeps the old one on screen meanwhile.
  // A language switch does not: pages must never see the new language with the old address (a guide would
  // redirect to its new slug under the old language's prefix).
  useLayoutEffect(
    () =>
      history.listen(({ action, location }) => {
        if (withLanguage.current) setState({ action, location });
        else startTransition(() => setState({ action, location }));
      }),
    [history],
  );

  // Language switcher (or a ticket link setting its language): the same page at the new language's address.
  useEffect(() => {
    const onLanguage = (lng: string) => {
      const target = addressFor(history.location, toSupportedLanguage(lng));
      if (!target) return;
      withLanguage.current = true;
      try {
        history.replace(target, history.location.state);
      } finally {
        withLanguage.current = false;
      }
    };
    i18n.on('languageChanged', onLanguage);
    return () => i18n.off('languageChanged', onLanguage);
  }, [history]);

  // An address in another language (a shared link, the back button) switches the site to that language; a plain
  // address seen in another language moves to that language's address.
  useEffect(() => {
    const { lang, path } = splitLangPath(withoutSiteBase(state.location.pathname));
    if (keepsAddress(path)) return;
    if (lang) {
      if (lang !== currentLanguage()) void i18n.changeLanguage(lang);
      return;
    }
    const target = crawler ? null : addressFor(state.location, currentLanguage());
    if (target) history.replace(target, state.location.state);
  }, [history, state.location]);

  const lang = addressLanguage(state.location.pathname);
  const basename = `${SITE_BASE}${lang ? LANG_META[lang].prefix : ''}` || '/';

  return (
    <Router basename={basename} location={state.location} navigationType={state.action} navigator={history}>
      {children}
    </Router>
  );
}
